import { Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { SortableEvent } from "react-sortablejs";
import { RoleMembersItem, RolePersona } from "../../../types/apiTypes";
import { MembersUpdateValues } from "../../../types/companyTypes";
import { KeyRoles } from "../../../types/enums";
import { GetContactsForRoleBtn } from "../../ui/buttons";
import { SortableGroupList } from "./SortableGroupList";
import { MembersTableHeader } from "./CompanyRoleTableHeader";

export const personaContainerId = (
  role?: KeyRoles,
  personaId?: string | null,
) => (personaId ? `${role}__${personaId}` : role);

export const parseContainerId = (containerId: string) => {
  const [role, personaId] = containerId.split("__");
  return { role: role as KeyRoles, persona_id: personaId ?? null };
};

const TitlesRow = ({ titles }: { titles: { name: string }[] }) => {
  const {
    palette: { custom: c },
  } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(titles.length);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const containerWidth = container.offsetWidth;
    const children = Array.from(
      container.querySelectorAll<HTMLElement>(".title-chip"),
    );
    let totalWidth = 0;
    let count = 0;
    for (const child of children) {
      totalWidth += child.offsetWidth + 8;
      if (totalWidth + 120 > containerWidth) break;
      count++;
    }
    setVisibleCount(count || 1);
  }, [titles]);

  const hiddenCount = titles.length - visibleCount;
  const visibleTitles = isExpanded ? titles : titles.slice(0, visibleCount);

  return (
    <Stack
      ref={containerRef}
      sx={{
        flexDirection: "row",
        flexWrap: isExpanded ? "wrap" : "nowrap",
        gap: "8px",
        alignItems: "center",
        overflow: "hidden",
        width: "100%",
      }}
    >
      {visibleTitles.map((title) => (
        <Stack
          key={title.name}
          className="title-chip"
          sx={{
            minHeight: "24px",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            padding: "0 8px",
            flexShrink: 0,
            borderRadius: "6px",
            border: `1px solid ${c.brandBlueBorder}`,
            background: "#FFF",
          }}
        >
          <Typography
            sx={{
              color: c.titleChipText,
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: "Inter",
              fontSize: "13px",
              fontWeight: 500,
              lineHeight: "12px",
              letterSpacing: "0.3px",
              whiteSpace: "nowrap",
            }}
          >
            {title.name}
          </Typography>
        </Stack>
      ))}

      {!isExpanded && hiddenCount > 0 && (
        <Stack
          onClick={() => setIsExpanded(true)}
          sx={{
            flexDirection: "row",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              color: c.brandBlue,
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: "Inter",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "26px",
              letterSpacing: "0.25px",
              textTransform: "capitalize",
              whiteSpace: "nowrap",
            }}
          >
            Show more
          </Typography>
          <Stack
            sx={{
              padding: "0 6.5px",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "100px",
              background: c.brandBlue,
              minWidth: "20px",
              height: "20px",
            }}
          >
            <span
              style={{
                color: "#FFF",
                fontSize: "12px",
                fontWeight: 500,
                lineHeight: "20px",
                display: "block",
              }}
            >
              {hiddenCount}
            </span>
          </Stack>
        </Stack>
      )}

      {isExpanded && (
        <Typography
          onClick={() => setIsExpanded(false)}
          sx={{
            cursor: "pointer",
            color: c.brandBlue,
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontFamily: "Inter",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "26px",
            letterSpacing: "0.25px",
            textTransform: "capitalize",
            whiteSpace: "nowrap",
          }}
        >
          Show less
        </Typography>
      )}
    </Stack>
  );
};

type Props = {
  roleId: KeyRoles;
  account_id: string;
  persona: RolePersona;
  isEditMode: boolean;
  isDiscardChanges: boolean;
  scraping: KeyRoles | null;
  is_active: boolean;
  currentScraping?: KeyRoles;
  updatedValues: { [key: string]: MembersUpdateValues };
  updateHandler: (
    values?: MembersUpdateValues,
    allValues?: { [key: string]: MembersUpdateValues },
  ) => void;
  setCommitteeSelected: React.Dispatch<React.SetStateAction<number>>;
  setClusterSelected: React.Dispatch<React.SetStateAction<number>>;
  onAdd: (e: SortableEvent) => void;
  onUpdate: (e: SortableEvent) => void;
};

export const PersonaGroup = (props: Props) => {
  const {
    palette: { custom: c },
  } = useTheme();
  const {
    roleId,
    account_id,
    persona,
    isEditMode,
    isDiscardChanges,
    scraping,
    is_active,
    currentScraping,
    updatedValues,
    updateHandler,
    setCommitteeSelected,
    setClusterSelected,
    onAdd,
    onUpdate,
  } = props;

  const [list, setList] = useState<RoleMembersItem[]>(persona.items);

  useEffect(() => {
    setList(persona.items);
  }, [persona.items]);

  useEffect(() => {
    if (isDiscardChanges) setList(persona.items);
  }, [isDiscardChanges]);

  return (
    <Stack
      sx={{
        flexDirection: "column",
        alignItems: "flex-start",
        alignSelf: "stretch",
        borderRadius: "8px",
        border: "1px solid var(--border-card, #DEDEDE)",
        background: "var(--surface-card-default, #FFF)",
        overflow: "hidden",
      }}
    >
      <Stack
        className="no-drag"
        sx={{
          width: "100%",
          padding: "16px",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          gap: "12px",
          background: c.brandBlueLight,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Inter",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "19px",
            letterSpacing: "0.1px",
            textTransform: "capitalize",
            color: c.brandBlue,
          }}
        >
          {persona.name}
        </Typography>
        <Typography
          sx={{
            overflow: "hidden",
            color: c.brandBlueHover,
            textOverflow: "ellipsis",
            fontFamily: "Inter",
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "normal",
            letterSpacing: "0.3px",
          }}
        >
          {persona.description}
        </Typography>
        {persona.titles.length > 0 && <TitlesRow titles={persona.titles} />}
      </Stack>

      {list.length > 0 && (
        <Stack className="no-drag" sx={{ padding: "8px 16px 0" }}>
          <MembersTableHeader />
        </Stack>
      )}

      <SortableGroupList
        list={list}
        setList={setList}
        containerId={personaContainerId(roleId, persona.id)}
        isEditMode={isEditMode}
        updatedValues={updatedValues}
        updateHandler={updateHandler}
        setCommitteeSelected={setCommitteeSelected}
        setClusterSelected={setClusterSelected}
        onAdd={onAdd}
        onUpdate={onUpdate}
        emptySlot={
          <Stack
            className="no-drag"
            sx={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "24px 28px",
            }}
          >
            <Typography
              sx={{
                color: c.textDisabled,
                fontSize: "14px",
                lineHeight: "20px",
              }}
            >
              No contacts found
            </Typography>
            <GetContactsForRoleBtn
              account_id={account_id}
              role={roleId}
              isLoading={currentScraping?.includes(roleId) || !!scraping}
              isDisabled={
                [roleId, "scraping", "update"].some((el) =>
                  currentScraping?.includes(el),
                ) ||
                !!scraping ||
                !is_active
              }
            />
          </Stack>
        }
      />
    </Stack>
  );
};
